// The answer stream reader (ADR-0050 FR-14, FR-15).
//
// One module owns fetch, the reader loop, the idle timer, and the translation
// of transport failures into the axios-shaped error objects the existing error
// handling in chat.js already understands.
//
// EventSource is not an option, and the reason is not taste: it is GET-only and
// sets no headers, so it can carry neither the X-CSRFToken ADR-0049 requires on
// every POST nor a body — and the body holds context_blocks, which can contain
// 2 MB of extracted PDF text.

import { csrfHeaders } from './csrf.js';
import { createFrameParser, createAccumulator } from './sse_parser.js';

// 90 seconds of silence, not 280 seconds of duration. An answer that keeps
// flowing runs to completion however long it takes. Since ADR-0064 the server
// writes a `: keepalive` comment as soon as the response is committed and every
// 15 s while it waits for the model, so a slow answer is never silent: this
// timer now catches a dead connection, not a slow answer.
const IDLE_TIMEOUT_MS = 90000;

/**
 * A timer that aborts the request after IDLE_TIMEOUT_MS without a byte.
 * Restarted per chunk, not per request.
 */
function createIdleTimer(controller, timeoutMs) {
    let handle = null;
    const timer = {
        restart() {
            timer.stop();
            handle = setTimeout(() => {
                timer.expired = true;
                controller.abort();
            }, timeoutMs);
        },
        stop() {
            if (handle !== null) clearTimeout(handle);
            handle = null;
        },
        expired: false
    };
    return timer;
}

/** An HTTP failure that arrived before the stream, in the axios error shape. */
async function httpError(response) {
    const error = new Error(`Request failed with status ${response.status}`);
    let data = null;
    try {
        data = await response.json();
    } catch (parseError) {
        data = null;
    }
    error.response = { status: response.status, data };
    return error;
}

/**
 * An `error` frame's payload in the axios error shape, or a plain Error.
 *
 * Since ADR-0064 a failure after the response was committed arrives as an
 * `error` frame carrying the `status` it would have had as an HTTP response, so
 * describeRequestError() can still render the 503 outage sentence and ADR-0032
 * still stages a real http_status. A frame without `status` stays a plain
 * Error, with no `response` (FR-23).
 */
function frameError(data) {
    const error = new Error(data?.error || 'Stream error');
    if (Number.isInteger(data?.status)) {
        error.response = { status: data.status, data: { error: data.error } };
    }
    return error;
}

/** Stop reading a body we are about to abandon; never throws. */
async function cancelQuietly(reader) {
    try {
        await reader.cancel();
    } catch (cancelError) {
        // The connection is being abandoned either way.
    }
}

/**
 * Translate a transport failure into the shape describeRequestError() and
 * stageRequestFailure() already read, so neither has to change and ADR-0032's
 * http_status keeps working.
 *
 * A user abort and an idle abort surface as the same AbortError on the same
 * controller, so they are told apart by asking the *caller's* signal whether it
 * fired — the fact lives there, not in a boolean that could drift.
 */
function translateFailure(error, callerSignal) {
    if (error.response) return error;
    if (error.name !== 'AbortError') return error;
    const cancelled = new Error(
        callerSignal.aborted ? 'Request cancelled' : 'Request timed out'
    );
    cancelled.code = callerSignal.aborted ? 'ERR_CANCELED' : 'ECONNABORTED';
    return cancelled;
}

/**
 * Read the response body, frame it, and dispatch to the handlers.
 *
 * Decoding uses `{stream: true}`: without it a multi-byte character split
 * across chunks decodes as two replacement characters, so in a German answer
 * "für" arrives with junk where the umlaut was, perhaps once a day.
 */
async function readFrames(reader, handlers, timer) {
    const decoder = new TextDecoder();
    const parser = createFrameParser();
    const accumulator = createAccumulator();
    let sawText = false;
    for (;;) {
        const { value, done } = await reader.read();
        if (done) return;
        timer.restart();
        for (const frame of parser.push(decoder.decode(value, { stream: true }))) {
            if (frame.event === 'text') {
                sawText = true;
                handlers.onText(accumulator.append(frame.data.delta));
            } else if (frame.event === 'phase') {
                // ADR-0081: the server names what it is doing before the
                // first text. Optional, like every frame type added later:
                // a handler set without it, and any unknown event name,
                // falls through and is dropped.
                handlers.onPhase?.(frame.data?.phase);
            } else if (frame.event === 'done') {
                handlers.onDone(frame.data);
            } else if (frame.event === 'error') {
                // Before any text, a status-carrying failure is handled exactly
                // like the HTTP status it replaces: thrown to the caller's catch
                // (FR-21). After text, the partial answer stays and onError
                // renders below it (FR-22).
                if (!sawText && Number.isInteger(frame.data?.status)) {
                    await cancelQuietly(reader);
                    throw frameError(frame.data);
                }
                handlers.onError(frameError(frame.data));
            }
        }
    }
}

/**
 * POST a question and stream its answer.
 *
 * @param {{url: string, body: object, signal: AbortSignal}} target
 * @param {{onText: Function, onDone: Function, onError: Function,
 *          onPhase?: Function}} handlers
 */
export async function streamAnswer(target, handlers) {
    const controller = new AbortController();
    const timer = createIdleTimer(controller, IDLE_TIMEOUT_MS);
    target.signal.addEventListener('abort', () => controller.abort());
    try {
        timer.restart();
        const response = await fetch(target.url, {
            method: 'POST',
            headers: csrfHeaders({
                'Content-Type': 'application/json',
                Accept: 'text/event-stream'
            }),
            body: JSON.stringify(target.body),
            signal: controller.signal
        });
        if (!response.ok) throw await httpError(response);
        await readFrames(response.body.getReader(), handlers, timer);
    } catch (error) {
        throw translateFailure(error, target.signal);
    } finally {
        timer.stop();
    }
}
